import React, { useCallback, useEffect, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { FaSave, FaTruck } from 'react-icons/fa';
import { API_BASE, parseJsonResponse, getUserErrorMessage } from '../utils/apiBase';
import { authFetch } from '../utils/apiAuth';
import NumericInput from '../components/NumericInput';

const DEFAULT_DELIVERY = {
  freeDeliveryEnabled: true,
  freeDeliveryThreshold: 4000,
  deliveryCharge: 280,
};

function formatRs(value) {
  return `Rs ${Number(value || 0).toLocaleString('en-PK')}`;
}

function previewShipping(subtotal, settings) {
  if (subtotal <= 0) {
    return 0;
  }
  if (!settings.freeDeliveryEnabled) {
    return settings.deliveryCharge;
  }
  return subtotal >= settings.freeDeliveryThreshold ? 0 : settings.deliveryCharge;
}

export default function DeliverySettings() {
  const [settings, setSettings] = useState(DEFAULT_DELIVERY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/v1/settings/delivery`);
      const result = await parseJsonResponse(response);
      setSettings({ ...DEFAULT_DELIVERY, ...(result.data || {}) });
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to load delivery settings.'));
      setSettings(DEFAULT_DELIVERY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    if (settings.deliveryCharge === '' || (settings.freeDeliveryEnabled && settings.freeDeliveryThreshold === '')) {
      toast.error('Please fill delivery amount fields');
      return;
    }
    setSaving(true);
    try {
      const response = await authFetch(`${API_BASE}/v1/settings/delivery`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          freeDeliveryEnabled: settings.freeDeliveryEnabled,
          freeDeliveryThreshold: Number(settings.freeDeliveryThreshold),
          deliveryCharge: Number(settings.deliveryCharge),
        }),
      });
      const result = await parseJsonResponse(response);
      setSettings({ ...DEFAULT_DELIVERY, ...(result.data || {}) });
      toast.success('Delivery settings saved');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to save delivery settings.'));
    } finally {
      setSaving(false);
    }
  };

  const threshold = Number(settings.freeDeliveryThreshold) || 0;
  const charge = Number(settings.deliveryCharge) || 0;
  const belowExample = Math.max(0, threshold - 500);
  const aboveExample = threshold + 500;

  return (
    <div style={styles.container}>
      <Toaster position="top-center" />

      <div style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>Delivery Settings</h1>
          <p style={styles.headerSubtitle}>
            Control free delivery rules for the website checkout — all across Pakistan
          </p>
        </div>
        <button
          type="button"
          style={styles.saveBtn}
          onClick={handleSave}
          disabled={
            loading ||
            saving ||
            settings.deliveryCharge === '' ||
            (settings.freeDeliveryEnabled && settings.freeDeliveryThreshold === '')
          }
        >
          <FaSave style={{ marginRight: 8 }} />
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
      </div>

      <div style={styles.grid}>
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <FaTruck style={{ marginRight: 8 }} />
            Free Delivery Rules
          </h2>

          {loading ? (
            <p style={styles.muted}>Loading settings…</p>
          ) : (
            <>
              <label style={styles.checkLabel}>
                <input
                  type="checkbox"
                  checked={settings.freeDeliveryEnabled}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, freeDeliveryEnabled: e.target.checked }))
                  }
                  style={styles.checkbox}
                />
                <span>Enable free delivery on website</span>
              </label>

              <div style={styles.fieldGrid}>
                <label style={styles.fieldLabel}>
                  Minimum order for free delivery (Rs or more)
                  <NumericInput
                    min="0"
                    step="1"
                    style={styles.input}
                    placeholder="e.g. 4000"
                    value={settings.freeDeliveryThreshold}
                    disabled={!settings.freeDeliveryEnabled}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        freeDeliveryThreshold: e.target.value,
                      }))
                    }
                  />
                  <span style={styles.fieldHint}>Example: 3000 means orders of Rs 3,000 or more get free delivery</span>
                </label>

                <label style={styles.fieldLabel}>
                  Delivery charge (below free limit)
                  <NumericInput
                    min="0"
                    step="1"
                    style={styles.input}
                    placeholder="e.g. 280"
                    value={settings.deliveryCharge}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        deliveryCharge: e.target.value,
                      }))
                    }
                  />
                  <span style={styles.fieldHint}>Charged at checkout when order is not eligible for free delivery</span>
                </label>
              </div>
            </>
          )}
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Live Preview</h2>
          <p style={styles.previewIntro}>
            This is how shipping will calculate on the website checkout page.
          </p>

          <div style={styles.previewCard}>
            <div style={styles.previewRow}>
              <span>Order {formatRs(belowExample)}</span>
              <strong>{formatRs(previewShipping(belowExample, settings))}</strong>
            </div>
            <div style={styles.previewRow}>
              <span>Order {formatRs(threshold)}</span>
              <strong>{formatRs(previewShipping(threshold, settings))}</strong>
            </div>
            <div style={styles.previewRow}>
              <span>Order {formatRs(aboveExample)}</span>
              <strong style={{ color: previewShipping(aboveExample, settings) === 0 ? '#4ade80' : '#F9D978' }}>
                {previewShipping(aboveExample, settings) === 0 ? 'Free' : formatRs(previewShipping(aboveExample, settings))}
              </strong>
            </div>
          </div>

          <div style={styles.websiteMessage}>
            <span style={styles.websiteMessageLabel}>Website message</span>
            <p style={styles.websiteMessageText}>
              {settings.freeDeliveryEnabled
                ? `Free delivery on orders of ${formatRs(threshold)} or more`
                : `Delivery charge: ${formatRs(charge)} on all orders`}
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

const styles = {
  container: {
    fontFamily: "'Inter', -apple-system, sans-serif",
    backgroundColor: '#E5D0A5',
    color: '#000',
    padding: '24px',
    minHeight: '100vh',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  headerTitle: {
    fontSize: '1.375em',
    margin: 0,
    fontWeight: '700',
    color: '#000',
  },
  headerSubtitle: {
    fontSize: '0.95em',
    color: '#333',
    margin: '6px 0 0 0',
    maxWidth: '560px',
  },
  saveBtn: {
    backgroundColor: '#F9D978',
    color: '#000',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.9em',
    display: 'inline-flex',
    alignItems: 'center',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '20px',
  },
  section: {
    backgroundColor: '#000',
    padding: '24px',
    borderRadius: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
  },
  sectionTitle: {
    fontSize: '1rem',
    margin: '0 0 20px',
    color: '#F9D978',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
  },
  muted: {
    color: '#888',
    margin: 0,
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#E5D0A5',
    fontWeight: '600',
    fontSize: '0.9em',
    cursor: 'pointer',
    marginBottom: '20px',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    accentColor: '#F9D978',
  },
  fieldGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  fieldLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    color: '#E5D0A5',
    fontSize: '0.85rem',
    fontWeight: '600',
  },
  fieldHint: {
    color: '#888',
    fontSize: '0.75rem',
    fontWeight: '400',
    lineHeight: 1.4,
  },
  input: {
    backgroundColor: '#111',
    border: '1px solid rgba(249, 217, 120, 0.5)',
    color: '#F9D978',
    borderRadius: '8px',
    padding: '12px 14px',
    fontSize: '1rem',
    outline: 'none',
  },
  previewIntro: {
    margin: '0 0 16px',
    color: '#888',
    fontSize: '0.85rem',
    lineHeight: 1.5,
  },
  previewCard: {
    backgroundColor: '#111',
    borderRadius: '12px',
    border: '1px solid #222',
    overflow: 'hidden',
  },
  previewRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px',
    borderBottom: '1px solid #222',
    color: '#ccc',
    fontSize: '0.9rem',
  },
  websiteMessage: {
    marginTop: '16px',
    padding: '14px',
    borderRadius: '10px',
    backgroundColor: 'rgba(249, 217, 120, 0.12)',
    border: '1px solid rgba(249, 217, 120, 0.45)',
  },
  websiteMessageLabel: {
    display: 'block',
    color: '#888',
    fontSize: '0.75rem',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  websiteMessageText: {
    margin: 0,
    color: '#F9D978',
    fontWeight: '600',
    fontSize: '0.95rem',
  },
};
